import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import apiClient from '@/lib/api-client'
import { Button } from '@/components/ui/Button'
import { Card, CardContent } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Loader2, Plus, Edit2, Trash2, X, ChevronDown, ChevronUp, Paintbrush, Package, Upload, ImageIcon } from 'lucide-react'
import { useDashboardEmbedded } from '@/hooks/useDashboardEmbedded'

const materialSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  unit: z.string().default('pcs'),
  base_price: z.number().int().min(0).default(0),
  is_active: z.boolean().default(true),
  allows_selection: z.boolean().default(false),
  is_required: z.boolean().default(false),
  category: z.string().optional(),
})

const variantSchema = z.object({
  name: z.string().min(1, 'Variant name is required'),
  sku: z.string().optional(),
  price_adjustment: z.number().int().default(0),
  quantity_per_use: z.number().min(0).default(1),
  attributes: z.record(z.any()).optional(),
  is_active: z.boolean().default(true),
})

type MaterialFormData = z.infer<typeof materialSchema>
type VariantFormData = z.infer<typeof variantSchema>

interface MaterialVariant {
  id: string
  name: string
  sku: string | null
  price_adjustment: number
  quantity_per_use: number
  attributes: Record<string, any>
  is_active: boolean
  sort_order: number
}

interface Material {
  id: string
  name: string
  description: string | null
  unit: string
  base_price: number
  base_price_formatted: string
  is_active: boolean
  allows_selection: boolean
  is_required: boolean
  category: string | null
  image_url: string | null
  variants: MaterialVariant[]
}

export default function MaterialsManager() {
  const embedded = useDashboardEmbedded()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null)
  const [expandedMaterial, setExpandedMaterial] = useState<string | null>(null)
  const [showVariantForm, setShowVariantForm] = useState<string | null>(null)

  // Fetch materials (backend returns grouped: [{category, materials}, ...])
  const { data: groupedData, isLoading } = useQuery<Array<{ category: string; materials: Material[] }>>({
    queryKey: ['materials'],
    queryFn: async () => {
      const response = await apiClient.get('/user/materials')
      return response.data.data
    },
  })

  // Create material mutation
  const createMutation = useMutation({
    mutationFn: async (data: MaterialFormData & { variants?: VariantFormData[] }) => {
      const response = await apiClient.post('/user/materials', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materials'] })
      setShowForm(false)
    },
  })

  // Update material mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: MaterialFormData & { id: string }) => {
      const response = await apiClient.put(`/user/materials/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materials'] })
      setEditingMaterial(null)
    },
  })

  // Delete material mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/user/materials/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materials'] })
    },
  })

  // Add variant mutation
  const addVariantMutation = useMutation({
    mutationFn: async ({ materialId, data }: { materialId: string; data: VariantFormData }) => {
      const response = await apiClient.post(`/user/materials/${materialId}/variants`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materials'] })
      setShowVariantForm(null)
    },
  })

  // Delete variant mutation
  const deleteVariantMutation = useMutation({
    mutationFn: async ({ materialId, variantId }: { materialId: string; variantId: string }) => {
      await apiClient.delete(`/user/materials/${materialId}/variants/${variantId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materials'] })
    },
  })

  // Upload material image mutation
  const uploadImageMutation = useMutation({
    mutationFn: async ({ materialId, file }: { materialId: string; file: File }) => {
      const formData = new FormData()
      formData.append('image', file)
      const response = await apiClient.post(`/user/materials/${materialId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materials'] })
    },
  })

  // Delete material image mutation
  const deleteImageMutation = useMutation({
    mutationFn: async (materialId: string) => {
      await apiClient.delete(`/user/materials/${materialId}/image`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materials'] })
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  // Use backend-provided grouping for display
  const groupedMaterials: Record<string, Material[]> = groupedData?.reduce((acc, group) => {
    acc[group.category] = group.materials
    return acc
  }, {} as Record<string, Material[]>) || {}

  return (
    <div className="p-6 lg:p-8">
      <div className={`flex items-center justify-between ${embedded ? 'mb-6' : 'mb-8'}`}>
        {!embedded && (
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Materials</h1>
            <p className="text-gray-500">Manage materials and variants for your services</p>
          </div>
        )}
        {embedded && <div />}
        <Button onClick={() => setShowForm(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Material
        </Button>
      </div>

      {/* Materials List */}
      {Object.keys(groupedMaterials).length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-400 mb-4">No materials yet</p>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Material
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedMaterials).map(([category, categoryMaterials]) => (
            <div key={category}>
              <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Package className="w-5 h-5" />
                {category}
              </h2>
              <div className="space-y-3">
                {categoryMaterials.map((material) => (
                  <MaterialCard
                    key={material.id}
                    material={material}
                    isExpanded={expandedMaterial === material.id}
                    onToggleExpand={() => setExpandedMaterial(
                      expandedMaterial === material.id ? null : material.id
                    )}
                    onEdit={() => setEditingMaterial(material)}
                    onDelete={() => deleteMutation.mutate(material.id)}
                    onAddVariant={() => setShowVariantForm(material.id)}
                    onDeleteVariant={(variantId) => deleteVariantMutation.mutate({
                      materialId: material.id,
                      variantId,
                    })}
                    showVariantForm={showVariantForm === material.id}
                    onSubmitVariant={(data) => addVariantMutation.mutate({
                      materialId: material.id,
                      data,
                    })}
                    onCancelVariant={() => setShowVariantForm(null)}
                    isAddingVariant={addVariantMutation.isPending}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Material Modal */}
      {(showForm || editingMaterial) && (
        <MaterialFormModal
          material={editingMaterial}
          onSubmit={async (data) => {
            if (editingMaterial) {
              await updateMutation.mutateAsync({ id: editingMaterial.id, ...data })
            } else {
              await createMutation.mutateAsync(data)
            }
          }}
          onClose={() => {
            setShowForm(false)
            setEditingMaterial(null)
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
          onUploadImage={async (file) => {
            if (editingMaterial) {
              await uploadImageMutation.mutateAsync({ materialId: editingMaterial.id, file })
            }
          }}
          onDeleteImage={() => {
            if (editingMaterial) {
              deleteImageMutation.mutate(editingMaterial.id)
            }
          }}
          isUploadingImage={uploadImageMutation.isPending}
        />
      )}
    </div>
  )
}

interface MaterialCardProps {
  material: Material
  isExpanded: boolean
  onToggleExpand: () => void
  onEdit: () => void
  onDelete: () => void
  onAddVariant: () => void
  onDeleteVariant: (variantId: string) => void
  showVariantForm: boolean
  onSubmitVariant: (data: VariantFormData) => void
  onCancelVariant: () => void
  isAddingVariant: boolean
  isUploadingImage?: boolean
}

function MaterialCard({
  material,
  isExpanded,
  onToggleExpand,
  onEdit,
  onDelete,
  onAddVariant,
  onDeleteVariant,
  showVariantForm,
  onSubmitVariant,
  onCancelVariant,
  isAddingVariant,
  isUploadingImage,
}: MaterialCardProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const variantForm = useForm<VariantFormData>({
    resolver: zodResolver(variantSchema),
    defaultValues: {
      name: '',
      sku: '',
      price_adjustment: 0,
      quantity_per_use: 1,
      attributes: {},
      is_active: true,
    },
  })

  const handleVariantSubmit = variantForm.handleSubmit(async (data) => {
    await onSubmitVariant(data)
    variantForm.reset()
  })

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              {/* Material Image Thumbnail */}
              {material.image_url ? (
                <div className="relative w-12 h-12 rounded overflow-hidden bg-gray-100 flex-shrink-0">
                  <img
                    src={material.image_url}
                    alt={material.name}
                    className="w-full h-full object-cover"
                  />
                  {isUploadingImage && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Loader2 className="w-4 h-4 text-white animate-spin" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <ImageIcon className="w-5 h-5 text-gray-400" />
                </div>
              )}
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-gray-900">{material.name}</h3>
                  {!material.is_active && (
                    <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">
                      Inactive
                    </span>
                  )}
                  {material.is_required && (
                    <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-600 rounded">
                      Required
                    </span>
                  )}
                </div>
                {material.description && (
                  <p className="text-sm text-gray-600 mt-1">{material.description}</p>
                )}
                <div className="flex items-center gap-4 mt-2 text-sm">
                  <span className="font-medium text-primary">
                    €{((material.base_price ?? 0) / 100).toFixed(2)} / {material.unit}
                  </span>
                  {material.allows_selection && material.variants?.length > 0 && (
                    <span className="text-gray-500">
                      {material.variants?.length} variant{material.variants?.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {material.variants?.length > 0 && (
              <button
                type="button"
                onClick={onToggleExpand}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            )}
            <button
              type="button"
              onClick={onEdit}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            {showDeleteConfirm ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onDelete}
                  className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="p-2 text-gray-400 hover:text-red-500"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Variants Section */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-700 flex items-center gap-2">
                <Paintbrush className="w-4 h-4" />
                Variants
              </h4>
              {material.allows_selection && (
                <Button size="sm" variant="outline" onClick={onAddVariant}>
                  <Plus className="w-3 h-3 mr-1" />
                  Add Variant
                </Button>
              )}
            </div>

            {/* Add Variant Form */}
            {showVariantForm && (
              <form onSubmit={handleVariantSubmit} className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <Input
                    {...variantForm.register('name')}
                    placeholder="Variant name (e.g., Ruby Red)"
                  />
                  <Input
                    {...variantForm.register('sku')}
                    placeholder="SKU (optional)"
                  />
                  <Input
                    type="number"
                    {...variantForm.register('price_adjustment', { valueAsNumber: true })}
                    placeholder="Price adjustment (cents)"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={onCancelVariant}>
                    Cancel
                  </Button>
                  <Button type="submit" size="sm" disabled={isAddingVariant}>
                    {isAddingVariant ? 'Adding...' : 'Add Variant'}
                  </Button>
                </div>
              </form>
            )}

            {/* Variants List */}
            {material.variants?.length === 0 ? (
              <p className="text-sm text-gray-400">
                {material.allows_selection
                  ? 'No variants yet. Add variants like colors, brands, or sizes.'
                  : 'Enable "Allows Selection" to add variants.'}
              </p>
            ) : (
              <div className="space-y-2">
                {material.variants.map((variant) => (
                  <div
                    key={variant.id}
                    className="flex items-center justify-between p-2 bg-white border rounded"
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <span className="font-medium text-gray-900">{variant.name}</span>
                        {variant.sku && (
                          <span className="text-xs text-gray-400 ml-2">SKU: {variant.sku}</span>
                        )}
                      </div>
                      {variant.price_adjustment !== 0 && (
                        <span className="text-sm text-primary">
                          {variant.price_adjustment > 0 ? '+' : ''}
                          €{(variant.price_adjustment / 100).toFixed(2)}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => onDeleteVariant(variant.id)}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface MaterialFormModalProps {
  material: Material | null
  onSubmit: (data: MaterialFormData & { variants?: VariantFormData[] }) => Promise<void>
  onClose: () => void
  isLoading: boolean
  onUploadImage: (file: File) => Promise<void>
  onDeleteImage: () => void
  isUploadingImage: boolean
}

function MaterialFormModal({ material, onSubmit, onClose, isLoading, onUploadImage, onDeleteImage, isUploadingImage }: MaterialFormModalProps) {
  const [variants, setVariants] = useState<VariantFormData[]>(
    material?.variants?.map((v) => ({
      name: v.name,
      sku: v.sku || undefined,
      price_adjustment: v.price_adjustment,
      quantity_per_use: v.quantity_per_use,
      attributes: v.attributes || {},
      is_active: v.is_active,
    })) || []
  )

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    await onUploadImage(file)
  }

  const form = useForm<MaterialFormData>({
    resolver: zodResolver(materialSchema),
    defaultValues: {
      name: material?.name || '',
      description: material?.description || '',
      unit: material?.unit || 'pcs',
      base_price: material?.base_price || 0,
      is_active: material?.is_active ?? true,
      allows_selection: material?.allows_selection ?? false,
      is_required: material?.is_required ?? false,
      category: material?.category || '',
    },
  })

  const handleSubmit = form.handleSubmit(async (data) => {
    await onSubmit({
      ...data,
      variants: variants.filter((v) => v.name.trim()),
    })
  })

  const addVariant = () => {
    setVariants([...variants, {
      name: '',
      sku: '',
      price_adjustment: 0,
      quantity_per_use: 1,
      attributes: {},
      is_active: true,
    }])
  }

  const removeVariant = (index: number) => {
    setVariants(variants.filter((_, i) => i !== index))
  }

  const updateVariant = (index: number, field: string, value: any) => {
    const updated = [...variants]
    updated[index] = { ...updated[index], [field]: value }
    setVariants(updated)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">
            {material ? 'Edit Material' : 'Add Material'}
          </h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Material Image */}
          <div className="flex items-center gap-4">
            <div className="relative">
              {material?.image_url ? (
                <div className="relative w-20 h-20 rounded overflow-hidden bg-gray-100">
                  <img
                    src={material.image_url}
                    alt={material.name}
                    className="w-full h-full object-cover"
                  />
                  {isUploadingImage && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-20 h-20 rounded bg-gray-100 flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 text-gray-400" />
                </div>
              )}
            </div>
            <div>
              <input
                type="file"
                id="material-image"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
                disabled={isUploadingImage}
              />
              <label htmlFor="material-image">
                <Button type="button" variant="outline" asChild>
                  <span className="cursor-pointer">
                    <Upload className="w-4 h-4 mr-2" />
                    {isUploadingImage ? 'Uploading...' : material?.image_url ? 'Replace Image' : 'Upload Image'}
                  </span>
                </Button>
              </label>
              {material?.image_url && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onDeleteImage}
                  className="ml-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                >
                  Remove
                </Button>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Optional. Supports JPG, PNG, WebP.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <Input
              {...form.register('name')}
              placeholder="e.g., Gel Polish, Hair Color"
            />
            {form.formState.errors.name && (
              <p className="text-sm text-red-500 mt-1">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              {...form.register('description')}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              placeholder="Optional description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <Input
                {...form.register('category')}
                placeholder="e.g., nail_polish, hair_color"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit
              </label>
              <Input
                {...form.register('unit')}
                placeholder="pcs, ml, g"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Base Price (cents)
            </label>
            <Input
              type="number"
              {...form.register('base_price', { valueAsNumber: true })}
              placeholder="0"
            />
            <p className="text-xs text-gray-500 mt-1">
              Price per unit in cents (e.g., 500 = €5.00)
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="allows_selection"
                {...form.register('allows_selection')}
                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <label htmlFor="allows_selection" className="text-sm text-gray-700">
                Allow customers to select variants (colors, brands, etc.)
              </label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_required"
                {...form.register('is_required')}
                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <label htmlFor="is_required" className="text-sm text-gray-700">
                Customer must select this material for booking
              </label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                {...form.register('is_active')}
                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <label htmlFor="is_active" className="text-sm text-gray-700">
                Active (visible to customers)
              </label>
            </div>
          </div>

          {/* Variants Section */}
          {form.watch('allows_selection') && (
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-700">Variants</h4>
                <Button type="button" size="sm" variant="outline" onClick={addVariant}>
                  <Plus className="w-3 h-3 mr-1" />
                  Add Variant
                </Button>
              </div>

              {variants.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">
                  No variants added. Click "Add Variant" to create options like colors or brands.
                </p>
              ) : (
                <div className="space-y-3">
                  {variants.map((variant, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Variant {index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeVariant(index)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          value={variant.name}
                          onChange={(e) => updateVariant(index, 'name', e.target.value)}
                          placeholder="Name (e.g., Ruby Red)"
                          className="text-sm"
                        />
                        <Input
                          value={variant.sku || ''}
                          onChange={(e) => updateVariant(index, 'sku', e.target.value)}
                          placeholder="SKU (optional)"
                          className="text-sm"
                        />
                        <Input
                          type="number"
                          value={variant.price_adjustment}
                          onChange={(e) => updateVariant(index, 'price_adjustment', parseInt(e.target.value) || 0)}
                          placeholder="Price adj. (cents)"
                          className="text-sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : material ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
